import type { NodeTypeCapabilities } from '@/config/nodeTypes';
import { isValidHostname, isValidIpAddress, isValidIpv6Address, isValidPort } from '@/utils/validation';
import type { PleskCredentials } from './PleskSettingsSection';

/**
 * Length limits of a user-supplied agent API token (mirror dto.AgentTokenMin/MaxLength on the server)
 */
export const MIN_AGENT_TOKEN_LENGTH = 32;
export const MAX_AGENT_TOKEN_LENGTH = 256;

// Printable ASCII without space, same rule as the server
const AGENT_TOKEN_PATTERN = /^[\x21-\x7e]+$/;

/**
 * Values of the node form that need validation
 */
export interface NodeFormValues {
  name: string;
  hostname: string;
  ipAddress: string;
  /** Optional public IPv6 address ("" = none) */
  ipv6Address: string;
  agentPort: string;
  agentToken: string;
  plesk: PleskCredentials;
}

/**
 * Validation errors of the node form
 */
export interface NodeFormErrors {
  name?: string;
  hostname?: string;
  ip_address?: string;
  ipv6_address?: string;
  agent_port?: string;
  agent_token?: string;
  plesk?: Partial<Record<keyof PleskCredentials, string>>;
}

/**
 * Validate the node form. Returns an empty object when valid.
 *
 * Plesk credentials are only checked for node types that have them; login
 * and password are required on create only (edit keeps the stored ones).
 */
export function validateNodeForm(
  values: NodeFormValues,
  options: { isEditMode: boolean; capabilities: Pick<NodeTypeCapabilities, 'hasPleskCredentials'> }
): NodeFormErrors {
  const errors: NodeFormErrors = {};
  const name = values.name.trim();

  if (!name) {
    errors.name = 'Name is required';
  } else if (name.length < 2) {
    errors.name = 'Name must be at least 2 characters';
  } else if (values.name.length > 255) {
    errors.name = 'Name must be at most 255 characters';
  }

  if (!values.hostname.trim()) {
    errors.hostname = 'Hostname is required';
  } else if (!isValidHostname(values.hostname.trim())) {
    errors.hostname = 'Invalid hostname format';
  }

  if (!values.ipAddress.trim()) {
    errors.ip_address = 'IP address is required';
  } else if (!isValidIpAddress(values.ipAddress.trim())) {
    errors.ip_address = 'Invalid IP address format';
  }

  const ipv6Address = values.ipv6Address.trim();
  if (ipv6Address && !isValidIpv6Address(ipv6Address)) {
    errors.ipv6_address = 'Must be a public (global unicast) IPv6 address';
  }

  if (!isValidPort(values.agentPort)) {
    errors.agent_port = 'Port must be between 1 and 65535';
  }

  const token = values.agentToken.trim();
  if (
    token &&
    (token.length < MIN_AGENT_TOKEN_LENGTH ||
      token.length > MAX_AGENT_TOKEN_LENGTH ||
      !AGENT_TOKEN_PATTERN.test(token))
  ) {
    errors.agent_token = `Token must be ${MIN_AGENT_TOKEN_LENGTH}-${MAX_AGENT_TOKEN_LENGTH} printable ASCII characters without spaces`;
  }

  if (options.capabilities.hasPleskCredentials) {
    const plesk: NonNullable<NodeFormErrors['plesk']> = {};
    const host = values.plesk.host.trim();

    if (!host) {
      plesk.host = 'Plesk panel URL is required';
    } else if (!host.startsWith('https://')) {
      plesk.host = 'Plesk URL must start with https://';
    }
    if (!options.isEditMode && !values.plesk.login.trim()) {
      plesk.login = 'Plesk admin login is required';
    }
    if (!options.isEditMode && !values.plesk.password.trim()) {
      plesk.password = 'Plesk admin password is required';
    }
    if (Object.keys(plesk).length > 0) {
      errors.plesk = plesk;
    }
  }

  return errors;
}
